import { isPromise } from "radash";

import { isError } from "./errorHandling.ts";

/**
 * Memoizes function results with bounded LRU cache and optional TTL.
 * Automatically clears cache on promise rejections or errors.
 *
 * The cache is bounded by `maxSize` (LRU eviction) so callers with unbounded
 * key spaces (e.g. URL params) cannot leak memory across long-lived processes
 * (SSR Node servers, CI workers, …). Cache hits refresh recency.
 *
 * @param callback - Function to memoize
 * @param options.key - Custom cache key function (default: JSON.stringify args)
 * @param options.staleTime - Cache TTL in milliseconds (optional)
 * @param options.maxSize - Maximum cache entries before LRU eviction (default 100)
 * @example
 * const expensiveCalc = memoize((a, b) => a * b, { staleTime: 5000 });
 * const result = expensiveCalc(5, 10); // Cached for 5 seconds
 */
function memoize<ArgsType extends unknown[], Result>(
  callback: (...args: ArgsType) => Result,
  {
    key,
    staleTime,
    maxSize = 100,
  }: {
    key?: (...args: ArgsType) => unknown;
    maxSize?: number;
    staleTime?: number;
  } = {},
): (...args: ArgsType) => Result {
  const cache = new Map<unknown, Result>();
  const timers = new Map<unknown, ReturnType<typeof setTimeout>>();

  function evict(cacheKey: unknown) {
    cache.delete(cacheKey);
    const timer = timers.get(cacheKey);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(cacheKey);
    }
  }

  return (...args: ArgsType) => {
    const cacheKey = key?.(...args) ?? JSON.stringify(args);

    if (cache.has(cacheKey)) {
      // Map preserves insertion order; re-insert to mark as most-recently used.
      const cachedResult = cache.get(cacheKey) as Result;
      cache.delete(cacheKey);
      cache.set(cacheKey, cachedResult);
      return cachedResult;
    }

    const result = callback(...args);

    if (cache.size >= maxSize) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) {
        evict(oldestKey);
      }
    }

    cache.set(cacheKey, result);

    if (staleTime) {
      timers.set(
        cacheKey,
        setTimeout(() => evict(cacheKey), staleTime),
      );
    }

    if (isPromise(result)) {
      result
        .then((value) => {
          if (isError(value)) {
            evict(cacheKey);
          }
        })
        .catch((error: unknown) => {
          evict(cacheKey);
          throw error;
        });
    }

    return result;
  };
}

/**
 * Ensures a function executes only once and caches the result (singleton pattern).
 * Includes a reset() method to clear the cache. Auto-resets on promise rejections.
 *
 * @param callback - Function to execute once
 * @returns Wrapped function with reset() method
 * @example
 * const getConfig = singleton(() => fetchConfig());
 * const config = await getConfig(); // Only fetches once
 * getConfig.reset(); // Clear cache
 */
function singleton<Result>(callback: () => Result): {
  (): Result;
  reset: () => void;
} {
  let instance: Result | null = null;

  const wrapped = () => {
    if (instance) {
      return instance;
    }

    const result = callback();
    instance = result;

    if (result instanceof Promise) {
      result
        .then((value) => {
          if (isError(value)) {
            instance = null;
          }
        })
        .catch((error: unknown) => {
          instance = null;
          throw error;
        });
    }

    return instance;
  };

  wrapped.reset = () => {
    instance = null;
  };

  return wrapped;
}

export { memoize, singleton };
